import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import moment from 'moment';
import I18n from 'PMT/i18n/I18n';

import ApiManagerOptions from 'constants/ApiManagerOptions';

import { DateUtils, Validation } from 'PMT/utils';
import SecurityContext from 'utils/permissions/SecurityContext';

// -- constants
import ApiConstants from 'PMT/constants/ApiConstants';
import ViewConstants from 'constants/ViewConstants';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import RestaurantMultipart from 'PMT/entities/RestaurantMultipart';

// -- views
import RestaurantFormComponent from './RestaurantFormComponent';
import PdfViewerModal from 'PMT/components/pdfViewer/PdfViewerModal';
import Calendar from 'PMT/components/Calendar';
import {
  POSChooser,
  ImageDropzone,
  TextInput,
  TransferFrequencyChooser,
  SimplePhoneInput,
  EmailInput,
  CountryChooser,
  PdfDropzone,
} from 'PMT/components/form';

export default class LegalFormView extends RestaurantFormComponent {
  static propTypes = {
    restaurant: PropTypes.instanceOf(Restaurant),

    /**
     * @param RestaurantMultipart
     */
    onUpdate: PropTypes.func.isRequired,
  };

  state = this.getDefaultState(this.props.restaurant);

  //
  // RestaurantFormComponent override.
  //

  getDefaultState(restaurant: Restaurant) {
    const legal = restaurant.legal;

    return {
      legal: legal.copy(),
      legalStatuteFile: null,
      legalRegistrationProofFile: null,
      legalShareholderDeclarationFile: null,

      legalRepresentativeIdentityPdf: null,

      // UI state
      displayPdfModal: false,
      pdfModalTitle: null, // the title to set to the pdf modal
      pdfModalUrl: null, // the url of the pdf to display on the pdf modal

      // form's warnings
      formWarnings: {},
    };
  }

  haveModifications() {
    const legal = this.props.restaurant.legal;
    const state = this.state;

    return !(
      legal.equals(state.legal) &&
      _.isNull(state.legalStatuteFile) &&
      _.isNull(state.legalRegistrationProofFile) &&
      _.isNull(state.legalShareholderDeclarationFile) &&
      _.isNull(state.legalRepresentativeIdentityPdf)
    );
  }

  //
  //
  //

  // checkMail(mail: string) {
  //   const regexMail = /([a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+)*)@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/;
  //   return regexMail.test(mail);
  // }

  updateLegalState(legal) {
    this.setState({
      legal,
    });
  }

  onShowPdfModal = (pdfModalTitle, pdfModalUrl) => {
    this.setState({
      displayPdfModal: true,
      pdfModalTitle, // the title to set to the pdf modal
      pdfModalUrl, // the url of the pdf to display on the pdf modal
    });
  };

  onHidePdfModal = () => {
    this.setState({
      displayPdfModal: false,
      pdfModalTitle: null, // the title to set to the pdf modal
      pdfModalUrl: null, // the url of the pdf to display on the pdf modal
    });
  };

  //
  // form actions
  //

  //
  //
  //

  onRestaurantEmailChange = (restaurantEmail: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.email = restaurantEmail;
    this.updateLegalState(legal);

    let formWarnings = null;
    if (!Validation.email(restaurantEmail)) {
      formWarnings = {
        restaurantMail: `Votre email n'est pas au bon format`,
      };
    }

    this.setState({
      formWarnings,
    });
  };

  onLegalManagerNameChange = (legalManagerName: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.managerName = legalManagerName;
    this.updateLegalState(legal);
  };

  onLegalManagerPhoneChange = (legalManagerPhone: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.managerPhone = legalManagerPhone;
    this.updateLegalState(legal);
  };

  //
  // Society
  //

  onLegalSirenChange = (legalSiren: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.siren = legalSiren;
    this.updateLegalState(legal);
  };

  onLegalVatNumberChange = (vatNumber: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.vatNumber = vatNumber;
    this.updateLegalState(legal);
  };

  onLegalCompanyNameChange = (legalCompanyName: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.name = legalCompanyName;
    this.updateLegalState(legal);
  };

  onLegalCompanyAddressStreetChange = (street: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.address.street = street;
    this.updateLegalState(legal);
  };

  onLegalCompanyAddressPostCodeChange = (postCode: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.address.postCode = postCode;
    this.updateLegalState(legal);
  };

  onLegalCompanyAddressCityChange = city => {
    const legal: RestaurantLegal = this.state.legal;
    legal.address.city = city;
    this.updateLegalState(legal);
  };

  onLegalCompanyAddressCountryChange = (country: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.address.country = country;
    this.updateLegalState(legal);
  };

  onLegalStatuteChange = legalStatuteFile => {
    this.setState({
      legalStatuteFile,
    });
  };

  onLegalRegistrationProofChange = legalRegistrationProofFile => {
    this.setState({
      legalRegistrationProofFile,
    });
  };

  onLegalShareholderDeclarationChange = legalShareholderDeclarationFile => {
    this.setState({
      legalShareholderDeclarationFile,
    });
  };

  //
  //
  //

  onLegalRepresentativeFirstNameChange = (firstName: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.firstName = firstName;
    this.updateLegalState(legal);
  };

  onLegalRepresentativeLastNameChange = (lastName: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.lastName = lastName;
    this.updateLegalState(legal);
  };

  onLegalRepresentativeEmailChange = (email: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.email = email;
    this.updateLegalState(legal);

    let formWarnings = null;
    if (!Validation.email(email)) {
      formWarnings = {
        representativeMail: `Votre email n'est pas au bon format`,
      };
    }

    this.setState({
      formWarnings,
    });
  };

  onLegalRepresentativeBirthdateChange = birthdate => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.birthday = birthdate.valueOf();
    this.updateLegalState(legal);
  };

  onLegalRepresentativeNationalityChange = (nationality: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.nationality = nationality;
    this.updateLegalState(legal);
  };

  onLegalRepresentativeCountryOfResidenceChange = (countryOfResidence: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.countryOfResidence = countryOfResidence;
    this.updateLegalState(legal);
  };

  onLegalRepresentativeAddressStreetChange = (street: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.address.street = street;
    this.updateLegalState(legal);
  };

  onLegalRepresentativePostCodeChange = (postCode: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.address.postCode = postCode;
    this.updateLegalState(legal);
  };

  onLegalRepresentativeCityChange = (city: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.address.city = city;
    this.updateLegalState(legal);
  };

  onLegalRepresentativeCountryChange = (country: string) => {
    const legal: RestaurantLegal = this.state.legal;
    legal.representative.address.country = country;
    this.updateLegalState(legal);
  };

  onLegalRepresentativeIdentityPdfChange = legalRepresentativeIdentityPdf => {
    this.setState({
      legalRepresentativeIdentityPdf,
    });
  };

  //
  //
  //

  onSubmit = event => {
    event.preventDefault();

    if (!_.isEmpty(this.state.formWarnings)) {
      return;
    }

    const restaurantMultipart: RestaurantMultipart = new RestaurantMultipart();
    restaurantMultipart.legal = this.state.legal.copy();

    // set files
    restaurantMultipart.legalShareholderDeclarationFile =
      this.state.legalShareholderDeclarationFile;
    restaurantMultipart.legalRegistrationProofFile = this.state.legalRegistrationProofFile;
    restaurantMultipart.legalStatuteFile = this.state.legalStatuteFile;
    restaurantMultipart.legalRepresentativeIdentityDocument =
      this.state.legalRepresentativeIdentityPdf;

    this.props.onUpdate(restaurantMultipart);
  };

  //
  // renderers
  //

  //
  // Restaurateur
  //

  renderLegalManagerPhone() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.manager.phone')}</label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <SimplePhoneInput
              className="form-control"
              value={this.state.legal.managerPhone}
              onChange={this.onLegalManagerPhoneChange}
              readOnly={!SecurityContext.restaurantSettings.editLegalRestaurateur()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-phone" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderLegalManagerName() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.manager.name')}</label>
        </div>

        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.managerName}
            onChange={this.onLegalManagerNameChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRestaurateur()}
          />
        </div>
      </div>
    );
  }

  renderEmail() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.manager.email')}
            {this.renderMangoPayIcon()}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              type="email"
              placeholder=""
              value={this.state.legal.email}
              onChange={this.onRestaurantEmailChange}
              readOnly={!SecurityContext.restaurantSettings.editLegalRestaurateur()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-envelope" />
              </label>
            </div>
          </div>
          {!_.isNil(this.state.formWarnings) &&
            !_.isEmpty(this.state.formWarnings.restaurantMail) && (
              <p className="alert alert-danger top-buffer-10">
                {this.state.formWarnings.restaurantMail}
              </p>
            )}
        </div>
      </div>
    );
  }

  //
  // Society
  //

  renderSiret() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.legal.siret')}</label>
        </div>

        <div className="col-xs-6">
          {_.isEmpty(this.state.legal.address.country) ? (
            <div style={{ marginTop: 8, marginBottom: 8, }}>
              {I18n.tr('restaurant_edit.legal.legal.countryRequired')}
            </div>
          ) : (
            <TextInput
              placeholder=""
              value={this.state.legal.siren}
              onChange={this.onLegalSirenChange}
              readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
            />
          )}
        </div>
      </div>
    );
  }

  isCountryForGeneratedVatNumber() {
    return [
      'FR',
    ].includes(this.state.legal.address.country);
  }

  renderVatNumber() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.legal.vatNumber')}</label>
        </div>

        <div className="col-xs-6">
          {_.isEmpty(this.state.legal.address.country) ? (
            <div style={{ marginTop: 8, marginBottom: 8, }}>
              {I18n.tr('restaurant_edit.legal.legal.countryRequired')}
            </div>
          ) : (
            <div>
              <TextInput
                placeholder=""
                value={this.state.legal.vatNumber}
                disabled={this.isCountryForGeneratedVatNumber()}
                onChange={this.onLegalVatNumberChange}
                readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
              />
              {this.isCountryForGeneratedVatNumber() && (
                <div style={{ marginTop: 8, marginBottom: 8, }}>
                  {I18n.tr('restaurant_edit.legal.legal.vatNumber.automaticallyCalculated')}
                </div>
              )}
            </div>
          
          )}
        </div>
      </div>
    );
  }

  renderLegalCompanyName() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.legal.company_name')}
            {this.renderMangoPayIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.name}
            onChange={this.onLegalCompanyNameChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
        </div>
      </div>
    );
  }

  renderLegalCompanyAddressStreet() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.legal.address.label')}
            {this.renderBankIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.address.street}
            onChange={this.onLegalCompanyAddressStreetChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
        </div>
      </div>
    );
  }

  renderLegalCompanyAddressPostCode() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.legal.address.post_code')}
            {this.renderBankIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.address.postCode}
            onChange={this.onLegalCompanyAddressPostCodeChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
        </div>
      </div>
    );
  }

  renderLegalCompanyAddressCity() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.legal.address.city')}
            {this.renderBankIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.address.city}
            onChange={this.onLegalCompanyAddressCityChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
        </div>
      </div>
    );
  }

  renderLegalCompanyAddressCountry() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.legal.address.country')}
            {this.renderBankIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <CountryChooser
            country={this.state.legal.address.country}
            onChange={this.onLegalCompanyAddressCountryChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
        </div>
      </div>
    );
  }

  renderLegalStatute() {
    const url = this.props.restaurant.legal.statute;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.status_pdf')}
            {this.renderKycIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <PdfDropzone
            onChange={this.onLegalStatuteChange}
            file={this.state.legalStatuteFile}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
          <br />
          {/* TODO: popin pdf */}
          {!_.isEmpty(url) && (
            <a
              className="link"
              onClick={() => {
                this.onShowPdfModal(I18n.tr('restaurant_edit.legal.status_pdf.modal_label'), url);
              }}
            >
              {I18n.tr('restaurant_edit.legal.status_pdf.see_file')}
            </a>
          )}
        </div>
      </div>
    );
  }

  renderLegalRegistrationProof() {
    const url = this.props.restaurant.legal.registrationProof;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.kbis')}

            {this.renderKycIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <PdfDropzone
            onChange={this.onLegalRegistrationProofChange}
            file={this.state.legalRegistrationProofFile}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
          <br />
          {/* TODO: popin pdf */}
          {!_.isEmpty(url) && (
            <a
              className="link"
              onClick={() => {
                this.onShowPdfModal(I18n.tr('restaurant_edit.legal.kbis.modal_label'), url);
              }}
            >
              {I18n.tr('restaurant_edit.legal.kbis.see_file')}
            </a>
          )}
        </div>
      </div>
    );
  }

  renderLegalShareholderDeclaration() {
    const url = this.props.restaurant.legal.shareholderDeclaration;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.shareholder_declaration')}

            {this.renderKycIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <PdfDropzone
            onChange={this.onLegalShareholderDeclarationChange}
            file={this.state.legalShareholderDeclarationFile}
            readOnly={!SecurityContext.restaurantSettings.editLegalSociety()}
          />
          <br />
          {!_.isEmpty(url) && (
            <a
              className="link"
              onClick={() => {
                this.onShowPdfModal(
                  I18n.tr('restaurant_edit.legal.shareholder_declaration.modal_label'),
                  url
                );
              }}
            >
              {I18n.tr('restaurant_edit.legal.shareholder_declaration.see_file')}
            </a>
          )}
          <br />
          {/* TODO: use right url */}
          {/* <a
            href="https://admin.dev.paymytable.com/pro/shareholder-declaration.pdf"
            target="_blank"
            className="link"
            >
            {I18n.tr('restaurant_edit.legal.shareholder_declaration.example')}
          </a> */}
        </div>
      </div>
    );
  }

  //
  // Legal representative
  //

  renderLegalRepresentativeFirstName() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.representative.first_name')}
            {this.renderMangoPayIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.representative.firstName}
            onChange={this.onLegalRepresentativeFirstNameChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativeLastName() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.representative.last_name')}
            {this.renderMangoPayIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.representative.lastName}
            onChange={this.onLegalRepresentativeLastNameChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativeBirthdate() {
    try {
      let defaultDate = moment().subtract(35, 'years');
      const minDate = moment().subtract(80, 'years');
      const maxDate = moment().subtract(18, 'years');

      if (this.state.legal.representative.birthday !== 0) {
        defaultDate = DateUtils.fromMsTimestamp(this.state.legal.representative.birthday);
      }

      // value={this.state.legal.representative.birthday}
      return (
        <div className="row form-group">
          <div className="col-xs-4 control-label">
            <label>
              {I18n.tr('restaurant_edit.legal.representative.birthdate')}

              {this.renderMangoPayIcon()}
            </label>
          </div>
          <div className="col-xs-6">
            <Calendar
              defaultDate={defaultDate}
              maxDate={maxDate}
              minDate={minDate}
              onChange={this.onLegalRepresentativeBirthdateChange}
              readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
              // inline={false}
            />
          </div>
        </div>
      );
    } catch (e) {
      console.error(e);
    }
  }

  renderLegalRepresentativeNationality() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.representative.nationality')}
            {this.renderMangoPayIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <CountryChooser
            country={this.state.legal.representative.nationality}
            onChange={this.onLegalRepresentativeNationalityChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativeCountryOfResidence() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.representative.country_of_residence')}
            {this.renderMangoPayIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <CountryChooser
            country={this.state.legal.representative.countryOfResidence}
            onChange={this.onLegalRepresentativeCountryOfResidenceChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativeEmail() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('global.email')}</label>
        </div>
        <div className="col-xs-6">
          <EmailInput
            placeholder=""
            value={this.state.legal.representative.email}
            onChange={this.onLegalRepresentativeEmailChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
          {!_.isNil(this.state.formWarnings) &&
            !_.isEmpty(this.state.formWarnings.representativeMail) && (
              <p className="alert alert-danger top-buffer-10">
                {this.state.formWarnings.representativeMail}
              </p>
            )}
        </div>
      </div>
    );
  }

  renderLegalRepresentativeAddressStreet() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.representative.address.label')}</label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.representative.address.street}
            onChange={this.onLegalRepresentativeAddressStreetChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativePostCode() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.representative.address.post_code')}</label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.representative.address.postCode}
            onChange={this.onLegalRepresentativePostCodeChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativeCity() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.representative.address.city')}</label>
        </div>
        <div className="col-xs-6">
          <TextInput
            placeholder=""
            value={this.state.legal.representative.address.city}
            onChange={this.onLegalRepresentativeCityChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativeCountry() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.legal.representative.address.country')}</label>
        </div>
        <div className="col-xs-6">
          <CountryChooser
            country={this.state.legal.representative.address.country}
            onChange={this.onLegalRepresentativeCountryChange}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
        </div>
      </div>
    );
  }

  renderLegalRepresentativeIdentityPdf() {
    const url = this.props.restaurant.legal.representative.identityDocument;
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.legal.representative.identity_document')}
            {this.renderKycIcon()}
          </label>
        </div>
        <div className="col-xs-6">
          <PdfDropzone
            onChange={this.onLegalRepresentativeIdentityPdfChange}
            file={this.state.legalRepresentativeIdentityPdf}
            readOnly={!SecurityContext.restaurantSettings.editLegalRepresentative()}
          />
          <br />
          {!_.isEmpty(url) && (
            <a
              className="link"
              onClick={() => {
                this.onShowPdfModal(
                  I18n.tr('restaurant_edit.legal.representative.identity_document.modal_label'),
                  url
                );
              }}
            >
              {I18n.tr('restaurant_edit.legal.representative.identity_document.see_file')}
            </a>
          )}
        </div>
      </div>
    );
  }

  renderPdfModal() {
    if (_.isNull(this.state.pdfModalUrl)) {
      return null;
    }

    const httpHeaders = ApiManagerOptions.headersMiddleware();

    return (
      <PdfViewerModal
        show={this.state.displayPdfModal}
        onHide={this.onHidePdfModal}
        file={this.state.pdfModalUrl}
        httpHeaders={httpHeaders}
        title={this.state.pdfModalTitle}
      />
    );
  }

  render() {
    const restaurant: Restaurant = this.props.restaurant;

    try {
      return (
        <div className="edit_restaurant_form_view__private">
          <form className="form-horizontal">
            {this.renderIconsLegend()}

            {SecurityContext.restaurantSettings.readLegalRestaurateur() && (
              <div>
                <h3 className="text-center">{I18n.tr('restaurant_edit.legal.restaurateur')}</h3>

                <div className="top-buffer-50">
                  {this.renderLegalManagerName()}
                  {this.renderLegalManagerPhone()}
                  {this.renderEmail()}
                </div>
                <hr className="top-buffer-50" />
              </div>
            )}

            {SecurityContext.restaurantSettings.readLegalSociety() && (
              <div className="top-buffer-50">
                <h3 className="text-center">{I18n.tr('restaurant_edit.legal.company')}</h3>

                <div className="top-buffer-50">
                  {this.renderLegalCompanyName()}
                  {this.renderLegalCompanyAddressStreet()}
                  {this.renderLegalCompanyAddressPostCode()}
                  {this.renderLegalCompanyAddressCity()}
                  {this.renderLegalCompanyAddressCountry()}

                  {this.renderSiret()}
                  {this.renderVatNumber()}

                  {this.renderLegalStatute()}
                  {this.renderLegalRegistrationProof()}
                  {this.renderLegalShareholderDeclaration()}
                </div>
                <hr className="top-buffer-50" />
              </div>
            )}

            {SecurityContext.restaurantSettings.readLegalRepresentative() && (
              <div className="top-buffer-50">
                <h3 className="text-center">
                  {I18n.tr('restaurant_edit.legal.legal_representative')}
                </h3>

                <div className="top-buffer-50">
                  {this.renderLegalRepresentativeFirstName()}
                  {this.renderLegalRepresentativeLastName()}
                  {this.renderLegalRepresentativeBirthdate()}
                  {this.renderLegalRepresentativeNationality()}
                  {this.renderLegalRepresentativeCountryOfResidence()}
                  {this.renderLegalRepresentativeAddressStreet()}
                  {this.renderLegalRepresentativePostCode()}
                  {this.renderLegalRepresentativeCity()}
                  {this.renderLegalRepresentativeCountry()}
                  {this.renderLegalRepresentativeEmail()}
                  {this.renderLegalRepresentativeIdentityPdf()}
                </div>
                <hr className="top-buffer-50" />
              </div>
            )}

            {this.renderButtons()}

            {this.renderPdfModal()}
          </form>
        </div>
      );
    } catch (e) {
      console.error(e);
    }
  }
}
